/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import { AccountBalance, AccountBalanceWallet, AdminPanelSettings, Approval, Assessment, BookmarkAdd, Business, Category, ChangeCircle, Close, ContentCopy, CorporateFare, CreditScore, Dashboard, DocumentScanner, ExpandLess, ExpandMore, FolderCopy, FolderShared, Help, Home, HowToReg, Inventory, LibraryBooks, Logout, MoveToInbox, Outbox, People, Person, Receipt, ReceiptLong, RecentActors, Rule, Settings, Warehouse } from '@mui/icons-material';
import { Toolbar, useTheme, Theme as MaterialTheme, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Drawer, Box, Button, Collapse, Dialog, DialogContent, TextField, InputAdornment, IconButton, DialogTitle, DialogActions, Badge } from '@mui/material';
import MainLogo from "../images/digiacc.png"
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IUserContextModel } from '../../service/model/IUserContextModel';
import { useContext, useEffect, useRef, useState } from 'react';
import UserContextService from '../../service/UserContextService';
import { IOrganizationModel } from '../../service/model/IOrganizationModel';
import OrganizationService from '../../service/OrganizationService';
import Grid from '@mui/material/Unstable_Grid2';
import { UserContext } from '../../layouts/AuthorizedLayout';
import AuthenticationService from '../../service/AuthenticationService';
import { toast } from 'react-toastify';
import axios from 'axios';
import { removeTokenFromLocalStorage } from '../../App';
import SourceDocumentBusinessLogic from '../../businessLogic/SourceDocumentBusinessLogic';
import { ISourceDocumentTypeStatisticsGuiModel } from '../../model/ISourceDocumentGuiModel';

interface IProps {
    mobileOpen: boolean,
    drawerToggle: () => void,
    drawerWidth: string,
}

interface ILinkElement {
    elements: IElement,
}

interface IElement {
    id: string,
    name: string,
    link: string,
    iconFunction: JSX.Element,
    index: number,
    subLinks?: IElement[],
    open?: boolean,
    handleOpen?: (value: boolean) => void,
    subRoute?: number,
    handleSubRoute?: (numb: number) => void,
    statistics?: ISourceDocumentTypeStatisticsGuiModel;
    customComponent?: JSX.Element;
}

const rootDrawer = (theme: MaterialTheme) => css({
    display: "flex",
    flexDirection: "column",
    height: "100%",
})
const logoMain = css({
    height: "1.6rem",
    filter: "brightness(0) invert(1)",
})

const menuIcon = css({
    color:"white",
})

const menuIconProfile = css({
    color: "black",
})

const Sidebar = (props: IProps): JSX.Element => {

    const { organizationId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    // State to hold the statistics data
    const [statistics, setStatistics] = useState<ISourceDocumentTypeStatisticsGuiModel | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);


    const user = useContext(UserContext);

    const [org, setOrg] = useState<IOrganizationModel>();
    const [orgError, setOrgError] = useState(false);
    const [orgs, setOrgs] = useState<IOrganizationModel[]>([]);
    const [orgsError, setOrgsError] = useState(false);

    const [orgSearch, setOrgSearch] = useState("");
    const [orgDialogOpen, setOrgDialogOpen] = useState(false);

    const hasVoucherRegistrationAccess = user?.user?.modules?.includes("VoucherRegistration");

    useEffect(() => {

        if (!organizationId || !hasVoucherRegistrationAccess) return;

        let isMounted = true;
        const service = new SourceDocumentBusinessLogic();

        const fetchStatistics = async () => {
            try {
                setLoading(true);
                const result = await service.getSourceDocumentTypeStatistics(organizationId);
                if (isMounted) {
                    setStatistics(result.value);
                }
            } catch (err) {
                if (isMounted) {
                    setError(t('sidemenu.statisticsFetchingError'));
                    toast.error(t('sidemenu.statisticsFetchingError'));
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchStatistics();

        const intervalId = setInterval(() => {
            fetchStatistics();
        }, 30000); 

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };

    }, [organizationId, !hasVoucherRegistrationAccess]);

    function handleOrgDialogClose() {
        setOrgDialogOpen(false);
    }
    function handleOrgDialogChosen(orgId: string) {
        setOrgDialogOpen(false);
        navigate("/firm/" + orgId);
    }

    useEffect(() => {
        let isMounted = true; //Avoid memory leaks. Async function running after dismount.
        const service = new OrganizationService();
        const getOrg = async () => {
            if (organizationId) {
                const result = await service.getOrganization(organizationId);
                if (!result.hasErrors) {
                    if (isMounted) {
                        setOrg(result.value);
                        setOrgError(false);
                    }
                } else {
                    setOrgError(true);
                }
            }            
        };
        const getOrgs = async () => {
            const result = await service.getOrganizations();
            if (!result.hasErrors) {
                if (isMounted) {
                    setOrgs(result.value);
                    setOrgsError(false);
                }
            } else {
                setOrgsError(true);
            }
        };
        getOrg();
        getOrgs();

        return () => { isMounted = false };

    }, [organizationId]);



    useEffect(() => {
        let isMounted = true; //Avoid memory leaks. Async function running after dismount.
        const service = new UserContextService();
        const getData = async () => {
            const result = await service.getUserContext(organizationId);
            if (!result.hasErrors) {
                if (isMounted) {
                    user.setUser(result.value);
                    i18n.changeLanguage(result.value.language?.replace(/-/g, ""));
                    user.setUserError([]);
                }
            } else {
                user.setUserError(result.errors);
            }
        };
        getData();

        return () => { isMounted = false };

    }, [organizationId]);

    useEffect(() => {

        if (location.pathname.includes("Approver"))
            setActiveRoute(1);
        else if (location.pathname.includes("Dashboard"))
            setActiveRoute(2);
        //else if (location.pathname.includes("Reports"))
        //    setActiveRoute(3);
        else if (location.pathname.includes("VoucherSearch"))
            setActiveRoute(3);
        else if (location.pathname.includes("VoucherRegistration"))
            setActiveRoute(4);
        else if (location.pathname.includes("Customers"))
            setActiveRoute(5);
        else if (location.pathname.includes("Suppliers"))
            setActiveRoute(6);
        else if (location.pathname.includes("Administration"))
            setActiveRoute(7);
        else if (location.pathname.includes("Profile"))
            setActiveRoute(8);
        else if (location.pathname.includes("Help"))
            setActiveRoute(9);
        else if (location.pathname.includes("Settings"))
            setActiveRoute(10);
        else
            setActiveRoute(0);

    }, [location])

    function userLogout() {

        const logout = async () => {
            axios.defaults.headers.common['Authorization'] = "";
            removeTokenFromLocalStorage();

            window.location.replace("/");

            //#################Ye olde code##############################
            //const authenticationService = new AuthenticationService();
            //const result1 = await authenticationService.postOldLogout();
            ////const result2 = await authenticationService.postLogout(); //Not in use!
            //if (result1.hasErrors) {
            //    toast(t("basic.cannotLogout"));
            //} else {
            //    toast(t("basic.loggedOut"));
            //    window.location.replace("https://home.digitalvnxt.no");
            //}
            //###########################################################
            
        }
        logout();
    }

    //##################################################################
    //########################Language Config###########################
    const { t, i18n } = useTranslation();

    //##################################################################
    //##################################################################
    const [activeRoute, setActiveRoute] = useState(0);
    //##################################################################
    //##################################################################
    const [activeDropDownApprover, setActiveDropDownApprover] = useState(false);
    function handleActiveDropDownApprover(value: boolean) {
        setActiveDropDownApprover(value)
    }
    const [activeSubRouteApprover, setActiveSubRouteApprover] = useState(0);
    function handleActiveSubRouteApprover(numb: number) {
        setActiveSubRouteApprover(numb)
    }
    //##################################################################
    const [activeDropDownVoucherSearch, setActiveDropDownVoucherSearch] = useState(false);
    function handleActiveDropDownVoucherSearch(value: boolean) {
        setActiveDropDownVoucherSearch(value)
    }
    const [activeSubRouteVoucherSearch, setActiveSubRouteVoucherSearch] = useState(0);
    function handleActiveSubRouteVoucherSearch(numb: number) {
        setActiveSubRouteVoucherSearch(numb)
    }
    //##################################################################
    const [activeDropDownReports, setActiveDropDownReports] = useState(false);
    function handleActiveDropDownReports(value: boolean) {
        setActiveDropDownReports(value)
    }
    const [activeSubRouteReports, setActiveSubRouteReports] = useState(0);
    function handleActiveSubRouteReports(numb: number) {
        setActiveSubRouteReports(numb)
    }
    //##################################################################
    const [activeDropDownSettings, setActiveDropDownSettings] = useState(false);
    function handleActiveDropDownSettings(value: boolean) {
        setActiveDropDownSettings(value)
    }
    const [activeSubRouteSettings, setActiveSubRouteSettings] = useState(0);
    function handleActiveSubRouteSettings(numb: number) {
        setActiveSubRouteSettings(numb)
    }
    //##################################################################

    const calculateTotalDocuments = (subLinks?: IElement[], statistics?: ISourceDocumentTypeStatisticsGuiModel) => {
        return subLinks
            ? subLinks.reduce((total, se) => {
                const count =
                se.id === 'Incoming' ? statistics?.incomingInvoice || 0 :
                se.id === 'Outgoing' ? statistics?.outgoingInvoice || 0 :
                se.id === 'Banking' ? statistics?.banking || 0 :
                se.id === 'Statement' ? statistics?.statement || 0 :
                se.id === 'JournalEntries' ? statistics?.journalEntries || 0 :
                se.id === 'Miscellaneous' ? statistics?.miscellaneous || 0 :
                se.id === 'Addition' ? statistics?.addition || 0 :
                se.id === 'Cash' ? statistics?.cash || 0 : 0;

                return total + count;
            }, 0)
            : 0;
    };

    //##################################################################

    const renderPrimaryTextWithBadge = (se: IElement, statistics?: ISourceDocumentTypeStatisticsGuiModel | null) => {

        const badgeContentMap: { [key: string]: number } = {
            Incoming: statistics?.incomingInvoice || 0,
            Outgoing: statistics?.outgoingInvoice || 0,
            Banking: statistics?.banking || 0,
            Statement: statistics?.statement || 0,
            JournalEntries: statistics?.journalEntries || 0,
            Miscellaneous: statistics?.miscellaneous || 0,
            Addition: statistics?.addition || 0,
            Cash: statistics?.cash || 0
        };

        return (
            <>
                {se.name}
                {badgeContentMap[se.id] !== undefined && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            position: 'absolute',
                            right: 1,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 'auto',
                        }}
                    >
                    <Badge
                        badgeContent={badgeContentMap[se.id]}
                        color="primary"
                        sx={{ marginRight: 10 }} 
                     />
                   </Box>
                )}
            </>
        );
    };

    const theme = useTheme();

    const LinkOrRedirect = (props: ILinkElement): JSX.Element => {

        const totalDocuments = calculateTotalDocuments(props.elements.subLinks, statistics || undefined);

        if (props.elements.subLinks) {
            return <>
                <ListItemButton
                    onClick={() => props.elements.handleOpen ? props.elements.handleOpen(!props.elements.open): undefined}
                    sx={{
                        paddingLeft: theme.spacing(1), // Reduce paddingLeft to move text closer to the icon
                        paddingRight: theme.spacing(2),
                        marginLeft: theme.spacing(1),
                        marginRight: theme.spacing(1),
                        marginTop: theme.spacing(1),
                        border: 1,
                        borderRadius: theme.shape.borderRadius,
                        bgcolor: props.elements.index == activeRoute ? theme.palette.primary.light : theme.palette.info.light,
                        borderColor: props.elements.index == activeRoute ? theme.palette.secondary.main : "black",
                        borderBottomRightRadius: props.elements.open ? 0 : theme.shape.borderRadius * 2,
                        position: 'relative', 
                    }}>
                    <ListItemIcon sx={{ minWidth: 35, fontSize: '1.5rem' }}>
                        {props.elements.iconFunction}
                    </ListItemIcon>
                    <ListItemText
                        primaryTypographyProps={{ color: "white" }}
                        primary={props.elements.name}
                        sx={{ flex: 1 }} 
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            position: 'absolute',
                            right: 5,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 'auto', 
                        }}
                    >
                        {totalDocuments > 0 && (
                            <Badge
                                badgeContent={totalDocuments}
                                color="primary"
                                sx={{ marginRight: 3 }} 
                            />
                        )}
                        {props.elements.open ? <ExpandLess css={menuIcon} /> : <ExpandMore css={menuIcon} />}
                    </Box>
                </ListItemButton>
                <Collapse in={props.elements.open} timeout="auto" unmountOnExit orientation="vertical">
                    <List component="div" disablePadding dense>
                        {props.elements.subLinks.map((se, index) => {
                            return <>
                                <ListItemButton component={Link} to={se.link} onClick={() => props.elements.handleSubRoute ? props.elements.handleSubRoute(se.index): undefined}
                                    sx={{
                                        marginLeft: theme.spacing(2), // Adjust marginLeft to align text and icons
                                        marginRight: theme.spacing(1),
                                        border: 1,
                                        borderBottomLeftRadius: props.elements.subLinks && index === props.elements.subLinks.length - 1 ? theme.shape.borderRadius * 2 : 0,
                                        borderBottomRightRadius: props.elements.subLinks && index === props.elements.subLinks.length - 1 ? theme.shape.borderRadius * 2 : 0,
                                        bgcolor: se.index === props.elements.subRoute && props.elements.index === activeRoute ? theme.palette.secondary.light : theme.palette.info.light,
                                        borderColor: se.index === props.elements.subRoute && props.elements.index === activeRoute ? theme.palette.secondary.main : "black",
                                        position: 'relative', 
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 35, fontSize: '1.5rem' }}> {/* Adjust size for consistency */}
                                        {se.iconFunction}
                                    </ListItemIcon>
                                    <ListItemText
                                        primaryTypographyProps={{ color: 'white' }}
                                        primary={renderPrimaryTextWithBadge(se, statistics)}
                                    />
                                </ListItemButton>
                            </>
                        })}
                    </List>
                </Collapse>
            </>
        } else {
            return <ListItemButton component={Link} to={props.elements.link}
                sx={{
                    paddingLeft: theme.spacing(1), // Reduce paddingLeft to move text closer to the icon
                    paddingRight: theme.spacing(2),
                    marginLeft: theme.spacing(1),
                    marginRight: theme.spacing(1),
                    marginTop: theme.spacing(1),
                    border: 1,
                    borderRadius: theme.shape.borderRadius,
                    bgcolor: props.elements.index == activeRoute ? theme.palette.primary.light : theme.palette.info.light,
                    borderColor: props.elements.index == activeRoute ? theme.palette.secondary.main : "black",
                    borderBottomRightRadius: props.elements.open ? 0 : theme.shape.borderRadius * 2,
                    position: 'relative', // Ensure badge is positioned correctly
                }}>
                <ListItemIcon sx={{ minWidth: 35, fontSize: '1.5rem' }}>
                    {props.elements.iconFunction}
                </ListItemIcon>
                <ListItemText
                    primaryTypographyProps={{ color: "white" }}
                    primary={props.elements.name}
                    sx={{ flex: 1 }} // Allow text to take up remaining space
                />
            </ListItemButton>
        }
    }

    //User control. Deactivate during debug
    const ShowHideUserMenuElements = (props: ILinkElement): JSX.Element => {
        if (props.elements.id === "Home") {
            return <LinkOrRedirect elements={props.elements} />
        } else if ((props.elements.id === "Customers" || props.elements.id === "Suppliers" || props.elements.id === "Profile") && (user.user.modules?.find(n => n === "VoucherRegistration" || n === "Approver"))) {
            return <LinkOrRedirect elements={props.elements} />
        } else if (user.user.modules?.find(n => n === props.elements.id)) {
            return <LinkOrRedirect elements={props.elements} />
        } else {
            return <></>
        }
    }

    //All buttons available. Deactivate during live run.
    //function ShowHideUserMenuElements(e: linkElement) {
    //    return LinkOrRedirect(e)
    //}

    const drawer = (
        <div css={rootDrawer(theme)}>
            <Box
                component={Button}
                sx={{
                    display: { xs: "none", xl: "flex" },
                    justifyContent: "center",
                    alignItems: "center",
                    height: "4rem",
                    width: "100%",
                }}>
                <img src={MainLogo} css={logoMain} alt="Digital Accounting logo" />
            </Box>
            <Toolbar sx={{ display: {xs:"block", xl:"none"} }}/>
            <Divider sx={{ marginLeft: theme.spacing(2), marginRight: theme.spacing(2), bgcolor: theme.palette.secondary.main }} />
            <List dense>
                <ShowHideUserMenuElements elements={{
                    id: "Home",
                    name: t('sidemenu.home'), link: "/firm/" + organizationId + "/",
                    iconFunction: < Home fontSize="small" css={menuIcon} />,
                    index: 0
                }} />
                {/*<ListItemButton onClick={() => { window.location.replace("/Firm/" + organizationId + "/Approver/Document"); }}*/}
                {/*    sx={{*/}
                {/*        marginLeft: theme.spacing(1), marginRight: theme.spacing(1), marginTop: theme.spacing(1),*/}
                {/*        border: 1,*/}
                {/*        borderRadius: theme.shape.borderRadius,*/}
                {/*        bgcolor: theme.palette.info.light*/}
                {/*    }}>*/}
                {/*    <ListItemIcon >*/}
                {/*        <Approval fontSize="large" css={menuIcon} />*/}
                {/*    </ListItemIcon>*/}
                {/*    <ListItemText primaryTypographyProps={{ color: "white" }} primary={t('sidemenu.approver')} />*/}
                {/*</ListItemButton>*/}
                <ShowHideUserMenuElements elements=
                    {{
                        id: "Approver",
                        name: t('sidemenu.approver'), link: "/firm/" + organizationId + "/Approver/Document",
                        iconFunction: < Approval fontSize="small" css={menuIcon} />,
                        index: 1,
                        subLinks:
                            [
                                {
                                    id: "MyDocuments",
                                    name: t('approver.myDocuments'), link: "/firm/" + organizationId + "/Approver/MyDocuments",
                                    iconFunction: <FolderShared fontSize="small" css={menuIcon} />,
                                    index: 0,
                                },
                                {
                                    id: "MyPreviousDocuments",
                                    name: t('approver.myPreviousDocuments'), link: "/firm/" + organizationId + "/Approver/MyPreviousDocuments",
                                    iconFunction: <Inventory fontSize="small" css={menuIcon} />,
                                    index: 1,
                                },
                                {
                                    id: "AllDocuments",
                                    name: t('approver.allDocuments'), link: "/firm/" + organizationId + "/Approver/AllDocuments",
                                    iconFunction: <FolderCopy fontSize="small" css={menuIcon} />,
                                    index: 2,
                                },
                                {
                                    id: "ApprovalFlow",
                                    name: t('approver.approvalFlow'), link: "/firm/" + organizationId + "/Approver/ApprovalFlow",
                                    iconFunction: <Rule fontSize="small" css={menuIcon} />,
                                    index: 3,
                                },
                                {
                                    id: "Approvers",
                                    name: t('approver.approvers'), link: "/firm/" + organizationId + "/Approver/Approvers",
                                    iconFunction: <HowToReg fontSize="small" css={menuIcon} />,
                                    index: 4,
                                },
                            ],
                        open: activeDropDownApprover,
                        handleOpen: handleActiveDropDownApprover,
                        subRoute: activeSubRouteApprover,
                        handleSubRoute: handleActiveSubRouteApprover,
                    }} />
                <ShowHideUserMenuElements elements={
                    {
                        id: "Dashboard",
                        name: t('sidemenu.dashboard'), link: "/firm/" + organizationId + "/Dashboard",
                        iconFunction: <Dashboard fontSize="small" css={menuIcon} />,
                        index: 2
                    }
                } />
                {/*<ShowHideUserMenuElements elements={*/}
                {/*    {*/}
                {/*        id: "Reports",*/}
                {/*        name: t('sidemenu.reports'), link: "/firm/" + organizationId + "/Reports/Result",*/}
                {/*        iconFunction: <Assessment fontSize="large" css={menuIcon} />,*/}
                {/*        index: 3,*/}
                {/*        subLinks:*/}
                {/*            [*/}
                {/*                {*/}
                {/*                    id: "Results",*/}
                {/*                    name: t('reports.result'), link: "/firm/" + organizationId + "/Reports/Result",*/}
                {/*                    iconFunction: <Assessment fontSize="small" css={menuIcon} />,*/}
                {/*                    index: 0,*/}
                {/*                },*/}
                {/*                //{*/}
                {/*                //    id: "CashRegisterReport",*/}
                {/*                //    name: t('reports.cashRegisterReport'), link: "/firm/" + organizationId + "/Reports/CashRegister",*/}
                {/*                //    iconFunction: <Assessment fontSize="small" css={menuIcon} />,*/}
                {/*                //    index: 1,*/}
                {/*                //},*/}
                {/*                {*/}
                {/*                    id: "OpenEntriesCustomer",*/}
                {/*                    name: t('reports.openEntriesCustomer'), link: "/firm/" + organizationId + "/Reports/OpenEntries/Customer",*/}
                {/*                    iconFunction: <Assessment fontSize="small" css={menuIcon} />,*/}
                {/*                    index: 1,*/}
                {/*                },*/}
                {/*                {*/}
                {/*                    id: "OpenEntriesSupplier",*/}
                {/*                    name: t('reports.openEntrierSupplier'), link: "/firm/" + organizationId + "/Reports/OpenEntries/Supplier",*/}
                {/*                    iconFunction: <Assessment fontSize="small" css={menuIcon} />,*/}
                {/*                    index: 2,*/}
                {/*                },*/}
                {/*            ],*/}
                {/*        open: activeDropDownReports,*/}
                {/*        handleOpen: handleActiveDropDownReports,*/}
                {/*        subRoute: activeSubRouteReports,*/}
                {/*        handleSubRoute: handleActiveSubRouteReports,*/}
                {/*    }}/>*/}
                <ShowHideUserMenuElements elements={
                    {
                        id: "VoucherSearch",
                        name: t('sidemenu.voucherSearch'),
                        link: "/firm/" + organizationId + "/VoucherSearch",
                        iconFunction: <ReceiptLong fontSize="small" css={menuIcon} />,
                        index: 3
                    }} />
                {user.user.modules?.find(n => n === "VoucherRegistration") && <ShowHideUserMenuElements elements=
                    {{
                        id: "VoucherRegistration",
                        name: t('sidemenu.registerVoucher'), link: "/firm/" + organizationId + "/VoucherRegistration/",
                        iconFunction: < DocumentScanner fontSize="small" css={menuIcon} />,
                        index: 4,
                        subLinks:
                            [
                                {
                                    id: "Incoming",
                                    name: t('voucherRegistration.incoming'), link: "/firm/" + organizationId + "/VoucherRegistration/Incoming",
                                    iconFunction: <MoveToInbox fontSize="small" css={menuIcon} />,
                                    index: 0,
                                },
                                {
                                    id: "Outgoing",
                                    name: t('voucherRegistration.outgoing'), link: "/firm/" + organizationId + "/VoucherRegistration/Outgoing",
                                    iconFunction: <Outbox fontSize="small" css={menuIcon} />,
                                    index: 1,
                                },
                                {
                                    id: "Banking",
                                    name: t('voucherRegistration.banking'), link: "/firm/" + organizationId + "/VoucherRegistration/Banking",
                                    iconFunction: <AccountBalance fontSize="small" css={menuIcon} />,
                                    index: 2,
                                },
                                {
                                    id: "JournalEntries",
                                    name: t('voucherRegistration.journalEntries'), link: "/firm/" + organizationId + "/VoucherRegistration/JournalEntries",
                                    iconFunction: <LibraryBooks fontSize="small" css={menuIcon} />,
                                    index: 3,
                                },
                                {
                                    id: "Miscellaneous",
                                    name: t('voucherRegistration.miscellaneous'), link: "/firm/" + organizationId + "/VoucherRegistration/Miscellaneous",
                                    iconFunction: <ContentCopy fontSize="small" css={menuIcon} />,
                                    index: 4,
                                },
                                {
                                    id: "Addition",
                                    name: t('voucherRegistration.addition'), link: "/firm/" + organizationId + "/VoucherRegistration/Addition",
                                    iconFunction: <BookmarkAdd fontSize="small" css={menuIcon} />,
                                    index: 5,
                                },
                                {
                                    id: "Cash",
                                    name: t('voucherRegistration.cash'), link: "/firm/" + organizationId + "/VoucherRegistration/Cash",
                                    iconFunction: <AccountBalanceWallet fontSize="small" css={menuIcon} />,
                                    index: 6,
                                },
                                {
                                    id: "Statement",
                                    name: t('voucherRegistration.statement'), link: "/firm/" + organizationId + "/VoucherRegistration/Statement",
                                    iconFunction: <Receipt fontSize="small" css={menuIcon} />,
                                    index: 7,
                                },
                            ],
                        open: activeDropDownVoucherSearch,
                        handleOpen: handleActiveDropDownVoucherSearch,
                        subRoute: activeSubRouteVoucherSearch,
                        handleSubRoute: handleActiveSubRouteVoucherSearch,
                    }} />}
                <ShowHideUserMenuElements elements={
                    {
                        id: "Customers",
                        name: t('sidemenu.customers'),
                        link: "/firm/" + organizationId + "/Actors/Customers",
                        iconFunction: <RecentActors fontSize="small" css={menuIcon} />,
                        index: 5
                    }
                } />
                <ShowHideUserMenuElements elements={
                    {
                        id: "Suppliers",
                        name: t('sidemenu.suppliers'),
                        link: "/firm/" + organizationId + "/Actors/Suppliers",
                        iconFunction: <Warehouse fontSize="small" css={menuIcon} />,
                        index: 6
                    }
                } />
                
                <ShowHideUserMenuElements elements={
                    {
                        id: "Settings",
                        name: t('sidemenu.settings'), link: "/firm/" + organizationId + "/Reports/Result",
                        iconFunction: <Settings fontSize="small" css={menuIcon} />,
                        index: 10,
                        subLinks:
                            [
                                {
                                    id: "Organization",
                                    name: t('basic.organization'), link: "/firm/" + organizationId + "/Settings/Organization",
                                    iconFunction: <CorporateFare fontSize="small" css={menuIcon} />,
                                    index: 0,
                                },
                                {
                                    id: "People",
                                    name: t('basic.people'), link: "/firm/" + organizationId + "/Settings/People",
                                    iconFunction: <People fontSize="small" css={menuIcon} />,
                                    index: 1,
                                },
                                {
                                    id: "CashRegisterSettings",
                                    name: t('sidemenu.cashRegisterSettings'), link: "/firm/" + organizationId + "/Settings/CashRegisterSettings",
                                    iconFunction: <CreditScore fontSize="small" css={menuIcon} />,
                                    index: 2,
                                },
                                {
                                    id: "ProductGroups",
                                    name: t('sidemenu.productGroups'), link: "/firm/" + organizationId + "/Settings/ProductGroup",
                                    iconFunction: <Category fontSize="small" css={menuIcon} />,
                                    index: 3,
                                },
                            ],
                        open: activeDropDownSettings,
                        handleOpen: handleActiveDropDownSettings,
                        subRoute: activeSubRouteSettings,
                        handleSubRoute: handleActiveSubRouteSettings,
                    }} />
                

                

                {user.user.modules?.find(n => n === "Administration") && <ListItemButton onClick={() => { window.open('https://admin.digitalvnxt.no', '_blank'); }}
                    sx={{
                        paddingLeft: theme.spacing(1), // Reduce paddingLeft to move text closer to the icon
                        paddingRight: theme.spacing(2),
                        marginLeft: theme.spacing(1),
                        marginRight: theme.spacing(1),
                        marginTop: theme.spacing(1),
                        border: 1,
                        borderRadius: theme.shape.borderRadius,
                        bgcolor: theme.palette.info.light, 
                    }}>
                    <ListItemIcon sx={{ minWidth: 35, fontSize: '1.5rem' }}>
                        <AdminPanelSettings fontSize="small" css={menuIcon} />
                    </ListItemIcon>
                    <ListItemText
                        primaryTypographyProps={{ color: "white" }}
                        primary={t('sidemenu.administration')}
                        sx={{ flex: 1 }} 
                    />
                </ListItemButton>}
                {/*<ShowHideUserMenuElements elements={*/}
                {/*    {*/}
                {/*        id: "Administration",*/}
                {/*        name: t('sidemenu.administration'),*/}
                {/*        link: "/Administration/User",*/}
                {/*        iconFunction: <Settings fontSize="large" css={menuIcon} />,*/}
                {/*        index: 8*/}
                {/*    }}/>*/}
            </List>
            <Box flexGrow={1}/>
            {/*<Box*/}
            {/*    component={Button}*/}
            {/*    display="flex" justifyContent="center" alignItems="center"*/}
            {/*    height="4rem" width="100%" marginBottom={theme.spacing(1)} marginTop={theme.spacing(1)}>*/}
            {/*    <img src={ftLogo} css={logoSecondary} />*/}
            {/*</Box>*/}
            <Divider sx={{ marginLeft: theme.spacing(2), marginRight: theme.spacing(2), bgcolor: theme.palette.secondary.main } }/>
            <List dense disablePadding>
                <ListItem>
                    <ListItemText sx={{ textAlign: "center", }}
                        secondaryTypographyProps={{ color: "white" }} 
                        secondary={t('sidemenu.welcome') + " " + (user && user.user ? user.user.fullName : "")} />
                </ListItem>
                <ListItem>
                    <ListItemText sx={{ textAlign: "center", }}
                        primaryTypographyProps={{ color: "white" }}
                        primary={org?.name} />
                </ListItem>
                <ListItemButton onClick={() => setOrgDialogOpen(true)}
                    sx={{
                        padding: 0,
                        margin: theme.spacing(1),
                        border: 1,
                        borderRadius: theme.shape.borderRadius,
                        bgcolor: theme.palette.info.light
                    }}>
                    <ListItemIcon >
                        <ChangeCircle fontSize="large" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ color: "white" }} primary={t('basic.changeOrganization')} />
                </ListItemButton>
                <ListItemButton onClick={() => navigate(`/firm/${organizationId}/Account/Profile`)}
                    sx={{
                        padding: 0,
                        margin: theme.spacing(1),
                        border: 1,
                        borderRadius: theme.shape.borderRadius,
                        bgcolor: theme.palette.info.light
                    }}>
                    <ListItemIcon >
                        <Person fontSize="large" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ color: "white" }} primary={t('sidemenu.profile')} />
                </ListItemButton>

                {/*{user?.user?.modules.find(n => n === "Settings") ?*/}
                {/*    <ListItemButton onClick={() => { window.location.replace("/Firm/" + organizationId + "/Settings"); }}*/}
                {/*        sx={{*/}
                {/*            padding: 0,*/}
                {/*            margin: theme.spacing(1),*/}
                {/*            border: 1,*/}
                {/*            borderRadius: theme.shape.borderRadius,*/}
                {/*            bgcolor: theme.palette.info.light*/}
                {/*        }}>*/}
                {/*        <ListItemIcon >*/}
                {/*            <Business fontSize="large" />*/}
                {/*        </ListItemIcon>*/}
                {/*        <ListItemText primaryTypographyProps={{ color: "white" }} primary={t('sidemenu.companySettings')} />*/}
                {/*    </ListItemButton>*/}
                {/*    : <></>*/}
                {/*}*/}
                <ListItemButton onClick={() => { window.location.replace("/Guide.pdf"); }}
                    sx={{
                        padding: 0,
                        margin: theme.spacing(1),
                        border: 1,
                        borderRadius: theme.shape.borderRadius,
                        bgcolor: theme.palette.info.light,
                        display: 'none'
                    }}>
                    <ListItemIcon >
                        <Help fontSize="large" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ color: "white" }} primary={t('sidemenu.help')} />
                </ListItemButton>
                <ListItemButton onClick={userLogout}
                    sx={{
                        padding: 0,
                        margin: theme.spacing(1),
                        border: 1,
                        borderRadius: theme.shape.borderRadius,
                        bgcolor: theme.palette.info.light,
                    }}>
                    <ListItemIcon >
                        <Logout fontSize="large" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ color: "white" }} primary={t('basic.logout')} />
                </ListItemButton>
                {/*<ListItem>*/}
                {/*    <ListItemButton onClick={() => handleOpenLanguageDialog()}*/}
                {/*        sx={{ border: 1, borderRadius: theme.shape.borderRadius, bgcolor: theme.palette.info.light }}>*/}
                {/*        <ListItemIcon >*/}
                {/*            <Language fontSize="large" />*/}
                {/*        </ListItemIcon>*/}
                {/*        <ListItemText primaryTypographyProps={{ color: "white" }} primary={t('sidemenu.language')} />*/}
                {/*    </ListItemButton>*/}
                {/*</ListItem>*/}
            </List>
        </div>
        );

    return <>
        <Drawer
            variant="temporary"
            open={props.mobileOpen}
            onClose={props.drawerToggle} ModalProps={{ keepMounted: true, }}
            sx={{
                ".MuiPaper-root": { backgroundColor: theme.palette.info.main },
                display: { xs: 'block', xl: 'none' },
                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: props.drawerWidth, },
            }}>
            {drawer}
        </Drawer>
        <Drawer
            variant="permanent"
            sx={{
                ".MuiPaper-root": { backgroundColor: theme.palette.info.main },
                display: { xs: 'none', xl: 'block' },
                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: props.drawerWidth, },
            }}
            open
        >
            {drawer}
        </Drawer>
        <Dialog maxWidth="sm" fullWidth={true} onClose={handleOrgDialogClose} open={orgDialogOpen}>
            <DialogTitle sx={{ bgcolor: theme.palette.info.light}}>
                <TextField size="small" variant="outlined" color="warning" value={orgSearch} 
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: 'white',
                            },
                            '&:hover fieldset': {
                                borderColor: 'white',
                            },
                        },
                    }}
                    fullWidth
                    InputLabelProps={{ shrink: true, style: {color:"white"} }}
                    InputProps={{
                        style: { color: "white" },
                        endAdornment:
                            <InputAdornment position="end" component={IconButton} onClick={() => setOrgSearch("")}
                                sx={{ display: orgSearch ? "inherit" : "none" }}><Close /></InputAdornment>,
                    }}
                    onChange={(e) => setOrgSearch(e.target.value)}
                    id="orgSearchId" label={t('search.search')} />
            </DialogTitle>
            <DialogContent dividers sx={{ bgcolor: theme.palette.info.main, height: "20rem" }}>
                <Grid container direction="column">
                    <List dense>
                        {orgs.filter(e => e.name.toLowerCase().includes(orgSearch.toLowerCase())).map(e => {
                            return <ListItemButton onClick={() => handleOrgDialogChosen(e.id)} sx={{
                                marginBottom: theme.spacing(2),
                                border: 1, borderRadius: theme.shape.borderRadius,
                                bgcolor: theme.palette.secondary.dark,
                                borderColor: theme.palette.secondary.main,
                            }}>
                                <ListItemText primaryTypographyProps={{ color: "white" }} primary={e.name} />
                                <ListItemIcon >
                                    <ChangeCircle fontSize="large" sx={{color:"white"}} />
                                </ListItemIcon>
                            </ListItemButton>
                        })}
                    </List>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ bgcolor: theme.palette.info.light }}>
                <Button onClick={() => handleOrgDialogClose()} variant="contained" color="secondary">{t("basic.cancel")}</Button>
            </DialogActions>
        </Dialog>
    </>

}

export default Sidebar;